import { useState, useEffect, useCallback, useRef } from 'react';
import { FocusTrap } from 'focus-trap-react';
import { HiXMark, HiCheck, HiClipboard, HiRss, HiArrowTopRightOnSquare } from 'react-icons/hi2';
import type { Show } from '../../types';
import { config } from '../../config';

interface RssModalProps {
  isOpen: boolean;
  onClose: () => void;
  show: Show;
  baseRssUrl?: string;
}

export function RssModal({
  isOpen,
  onClose,
  show,
  baseRssUrl = config.rssBaseUrl,
}: RssModalProps) {
  const [copied, setCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // RSS URL for this show
  const rssUrl = `${baseRssUrl}/show/${show.showid}.rss`;

  // pcast:// URL for native podcast app subscription
  const pcastUrl = `pcast://${baseRssUrl.replace(/^https?:\/\//, '')}/show/${show.showid}.rss`;

  // Handle animation
  useEffect(() => {
    if (isOpen) {
      // Small delay to trigger animation
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const copyToClipboard = useCallback(async () => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    try {
      await navigator.clipboard.writeText(rssUrl);
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = rssUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }
  }, [rssUrl]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center
                  transition-colors duration-200
                  ${isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rss-modal-title"
    >
      <FocusTrap focusTrapOptions={{ initialFocus: false, allowOutsideClick: true }}>
      <div
        className={`bg-[var(--color-surface)] rounded-t-[var(--radius-2xl)] sm:rounded-[var(--radius-2xl)]
                   shadow-[var(--shadow-xl)] w-full sm:max-w-xs overflow-hidden
                   border border-[var(--color-border)]
                   transition-all duration-300 ease-out
                   ${isVisible
                     ? 'translate-y-0 opacity-100 scale-100'
                     : 'translate-y-8 opacity-0 scale-95'
                   }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <HiRss size={18} className="text-orange-500" />
            <h2 id="rss-modal-title" className="text-sm font-semibold text-[var(--color-text)]">
              Subscribe to Podcast
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 text-[var(--color-text-muted)]
                       hover:text-[var(--color-text)]
                       hover:bg-[var(--color-surface-elevated)]
                       transition-colors rounded-full
                       focus:outline-none focus-visible:ring-2
                       focus-visible:ring-[var(--color-primary)]"
          >
            <HiXMark size={18} />
          </button>
        </div>

        {/* Show title */}
        <div className="px-4 pb-3">
          <p className="text-xs text-[var(--color-text-muted)]">
            {show.title}
          </p>
        </div>

        {/* Open in Podcast App button */}
        <div className="px-4 pb-4">
          <a
            href={pcastUrl}
            className="flex items-center justify-center gap-2 w-full p-3
                       text-white font-medium text-sm
                       bg-[var(--color-primary)]
                       hover:opacity-90
                       rounded-[var(--radius-lg)] transition-opacity
                       focus:outline-none focus-visible:ring-2
                       focus-visible:ring-[var(--color-primary)]
                       focus-visible:ring-offset-2"
          >
            <HiArrowTopRightOnSquare size={16} />
            <span>Open in Podcast App</span>
          </a>
        </div>

        {/* Divider */}
        <div className="px-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[var(--color-border)]" />
            <span className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase">
              or copy feed URL
            </span>
            <div className="flex-1 h-px bg-[var(--color-border)]" />
          </div>
        </div>

        {/* Copy RSS URL */}
        <div className="p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={rssUrl}
              readOnly
              className="flex-1 px-3 py-2 text-xs
                         text-[var(--color-text)]
                         bg-[var(--color-surface-elevated)]
                         border border-[var(--color-border)]
                         rounded-[var(--radius-lg)]
                         focus:outline-none truncate"
            />
            <button
              onClick={copyToClipboard}
              aria-label={copied ? 'Copied!' : 'Copy RSS URL'}
              className={`px-3 py-2 text-xs font-medium rounded-[var(--radius-lg)]
                         transition-all duration-150
                         focus:outline-none focus-visible:ring-2
                         focus-visible:ring-[var(--color-primary)]
                         flex items-center gap-1.5 flex-shrink-0
                         ${copied
                           ? 'bg-green-500 text-white'
                           : 'bg-[var(--color-surface-elevated)] text-[var(--color-text)] hover:bg-[var(--color-border)]'
                         }`}
            >
              {copied ? (
                <>
                  <HiCheck size={14} />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <HiClipboard size={14} />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      </FocusTrap>
    </div>
  );
}
